import { Button, DatePicker, Form, Input, Select, Space } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect } from "react";
import CheckedIcon from "../../../assets/icons/CheckedIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import { dbDateFormat, timeFormat } from "../../../constants/constants";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import { useAddLeaveApplicationMutation } from "../../../features/leaveApplication/leaveApplicationApi";
import { useGetLeaveTypeListQuery } from "../../../features/leaveType/leaveTypeApi";
import { useShiftCalenderEmployeeQuery } from "../../../features/shiftAssignToEmployee/shiftAssignToEmployeeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { selectArrayOption, selectSearchOption } from "../../../utils/utils";
import SelectImgNameTitle from "../../common/SelectImgNameTitle";
import CustomModal from "../../ui/CustomModal";
const { RangePicker } = DatePicker;

type Props = {
  open: boolean;
  handleClose: () => void;
};
const AddLeaveRequestModal = ({ handleClose: parentClose, ...rest }: Props) => {
  const { open, handleOpen, handleClose } = useModal<undefined, "SUCCESS">();
  const [form] = Form.useForm();
  const dayType = Form.useWatch("day_type", form);
  const leavePeriod = Form.useWatch("leave_period", form);

  const employeeId = Form.useWatch("employee_id", form);

  const isHalfDayConfigure =
    employeeId &&
    leavePeriod &&
    leavePeriod?.[0]?.format(dbDateFormat) ===
      leavePeriod?.[1]?.format(dbDateFormat) &&
    dayType === "half-day";

  // console.log(isHalfDayConfigure, leavePeriod);

  const { isError, error, data, isLoading } = useShiftCalenderEmployeeQuery(
    {
      params: {
        business_member_id: employeeId,
        month_year: leavePeriod?.[0]?.format("YYYY-MM"),
      },
    },
    {
      skip: !isHalfDayConfigure,
    }
  );
  useError(isError, error, "Get shift calendar fail");

  const shiftData = data?.data?.calendar_data?.find(
    (el) => el?.date === leavePeriod?.[0]?.format(dbDateFormat)
  )?.half_day_configuration;

  // Queries
  const {
    isLoading: isEmpLoading,
    isError: isEmpError,
    error: empError,
    data: employees,
  } = useGetEmployeesQuery({
    params: { limit: 0, skip: 0, status: "active" },
  });
  const {
    data: leaveTypes,
    isError: isLeaveTypesError,
    error: leaveTypesError,
    isLoading: isLeaveTypesLoading,
  } = useGetLeaveTypeListQuery({
    params: {
      is_published: 1,
      skip: 0,
      limit: 0,
    },
  });

  // Mutations
  const [
    createLeave,
    {
      isLoading: creating,
      isError: iscCreateError,
      error: createError,
      isSuccess: created,
    },
  ] = useAddLeaveApplicationMutation();

  const handleFinish = (values: any) => {
    const finalData = {
      business_member_id: values?.employee_id,
      substitute_id: values?.substitute_id,
      leave_type_id: values?.leave_type_id,
      is_half_day: dayType === "half-day" ? 1 : 0,
      start_date: dayjs(values.leave_period?.[0]).format("YYYY-MM-DD"),
      end_date: dayjs(values.leave_period?.[1]).format("YYYY-MM-DD"),
      half_day_configuration: values?.half_day_configuration,
      note: values?.note,
    };

    createLeave(finalData);
  };

  // Errors
  useError(isEmpError, empError, "Employees get failed.");
  useError(isLeaveTypesError, leaveTypesError, "Leave Types get failed.");
  useError(iscCreateError, createError, "Leave Request create failed.");

  // Effects
  useEffect(() => {
    if (!leavePeriod || leavePeriod.length !== 2) {
      form.setFieldsValue({ total_days: "" });
      return;
    }

    if (dayType === "half-day") {
      form.setFieldsValue({ total_days: 0.5 });
    } else {
      const start = dayjs(leavePeriod[0]);
      const end = dayjs(leavePeriod[1]);
      const diff = end.diff(start, "day") + 1; // inclusive
      form.setFieldsValue({ total_days: diff });
    }
  }, [leavePeriod, dayType, form]);

  useEffect(() => {
    if (created) {
      handleOpen({ type: "SUCCESS" });
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);

  useEffect(() => {
    if (!isHalfDayConfigure) {
      form.setFieldsValue({ half_day_configuration: undefined });
    }
  }, [form, isHalfDayConfigure]);

  return (
    <div>
      {/* Success Modal */}
      {open?.type === "SUCCESS" && (
        <CustomModal
          title=""
          footer={null}
          width={400}
          closeIcon={null}
          {...rest}
        >
          <div className="flex justify-center text-center items-center">
            <div className="text-center">
              <div className="flex justify-center text-center items-center">
                <CheckedIcon />
              </div>
              <div className="text-center justify-center text-zinc-700 text-base font-medium py-5">
                Leave request added successfully!
              </div>

              <Button
                onClick={() => {
                  handleClose();
                  parentClose();
                }}
                type="primary"
                className="min-w-[100px]"
              >
                Okay
              </Button>
            </div>
          </div>
        </CustomModal>
      )}

      <CustomModal
        title="Add Request"
        handleClose={parentClose}
        width={800}
        footer={null}
        {...rest}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark={false}
          className="custom-form"
        >
          <Form.Item
            className="!mb-4"
            name="employee_id"
            label="Employee Name or ID"
            rules={[
              {
                required: true,
                message: "Please select an employee name or id",
              },
            ]}
          >
            <Select
              suffixIcon={<SelectDropdownLgIcon />}
              size="large"
              showSearch
              filterOption={selectSearchOption}
              disabled={isEmpLoading}
              options={employees?.data?.employees?.map((e) => ({
                label: `${e?.member?.profile?.name} ${
                  e?.employee_id ? `(${e?.employee_id})` : ""
                }`,
                value: e?.id,
              }))}
              placeholder="Select an employee name."
              optionFilterProp="label"
            />
          </Form.Item>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              className="!mb-4"
              name="leave_type_id"
              label="Leave Type"
              rules={[{ required: true, message: "Please select leave type" }]}
            >
              <Select
                suffixIcon={<SelectDropdownLgIcon />}
                size="large"
                placeholder="Select a leave type."
                disabled={isLeaveTypesLoading}
                options={selectArrayOption(
                  leaveTypes?.data?.leave_types || [],
                  "title",
                  "id"
                )}
              />
            </Form.Item>
            <Form.Item
              className="!mb-4"
              name="day_type"
              label="Day Type"
              rules={[{ required: true, message: "Please select day type" }]}
            >
              <Select
                suffixIcon={<SelectDropdownLgIcon />}
                size="large"
                options={[
                  { label: "Full Day", value: "full-day" },
                  { label: "Half Day", value: "half-day" },
                ]}
                placeholder="Select a leave type."
              />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 items-start gap-4">
            <Form.Item
              className="!mb-4"
              name="leave_period"
              label="Leave Period"
              rules={[
                { required: true, message: "Please select leave period" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || value.length !== 2) return Promise.resolve();

                    const [start, end] = value;
                    if (dayType === "half-day") {
                      if (!dayjs(start).isSame(end, "day")) {
                        return Promise.reject(
                          new Error("Half-day leave must be on the same day")
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <RangePicker
                className="!w-full"
                suffixIcon={<SelectDropdownLgIcon />}
                size="large"
                placeholder={["Start Date", "End Date"]}
              />
            </Form.Item>
            <div className="w-full flex items-center gap-4">
              {dayType === "half-day" ? (
                <Form.Item
                  className="!mb-4 w-[70%]"
                  name="half_day_configuration"
                  label="Half Day Configuration"
                  rules={[
                    {
                      required: true,
                      message: "Please select half day configuration",
                    },
                  ]}
                >
                  <Select
                    suffixIcon={<SelectDropdownLgIcon />}
                    size="large"
                    options={[
                      {
                        label: shiftData?.first_half?.start_time
                          ? `First Half (${moment(
                              shiftData?.first_half?.start_time,
                              "HH:mm:ss"
                            ).format(timeFormat)} - ${moment(
                              shiftData?.first_half?.end_time,
                              "HH:mm:ss"
                            ).format(timeFormat)})`
                          : "First Half",
                        value: "first_half",
                      },
                      {
                        label: shiftData?.second_half?.start_time
                          ? `Second Half (${moment(
                              shiftData?.second_half?.start_time,
                              "HH:mm:ss"
                            ).format(timeFormat)} - ${moment(
                              shiftData?.second_half?.end_time,
                              "HH:mm:ss"
                            ).format(timeFormat)})`
                          : "Second Half",
                        value: "second_half",
                      },
                    ]}
                    placeholder="Select"
                    disabled={!isHalfDayConfigure || isLoading}
                  />
                </Form.Item>
              ) : null}
              <Form.Item
                className={`!mb-4 flex-1 ${
                  dayType !== "half-day"
                    ? "!w-full !flex-1"
                    : "flex-1 !w-[85px]"
                }`}
                name="total_days"
                label="Total Days"
              >
                <Input size="large" disabled placeholder="0" />
              </Form.Item>
            </div>
          </div>
          <Form.Item className="!mb-4" name="note" label="Note">
            <Input.TextArea size="large" placeholder="Enter a description..." />
          </Form.Item>
          <Form.Item
            className="!mb-4"
            name="substitute_id"
            label="Substitute"
            // rules={[{ required: true, message: "Please select substitute" }]}
          >
            <SelectImgNameTitle
              disabled={isEmpLoading}
              placeholder="Select"
              data={employees?.data?.employees}
            />
          </Form.Item>
          <Space size={10} className="pt-4">
            <Button
              htmlType="button"
              className="min-w-[125px] h-10"
              onClick={() => {
                parentClose();
              }}
            >
              Cancel
            </Button>
            <Button
              loading={creating}
              className="min-w-[125px] h-10"
              htmlType="submit"
              type="primary"
            >
              Add Request
            </Button>
          </Space>
        </Form>
      </CustomModal>
    </div>
  );
};

export default AddLeaveRequestModal;
